import { getLocalStorageUserSha } from '@/utils/storage';
import useAuth from '@rhg/libs/auth0/use-auth';
import { invalidateQueryKey, queryKeys, useQuery } from '@rhg/libs/react-query';
import { daysAgo, getUtcDateForMDY, getYMD } from '@rhg/utils/date-time';
import { useCallback } from 'react';

const TRANSITION_DIALOG_DISMISSAL_KEY = 'tpdd';
const DISMISSAL_INTERVAL_DAYS = 7;

interface TransitionDialogDismissalData {
    isDismissalDue: boolean;
}

const placeholderData: TransitionDialogDismissalData = {
    isDismissalDue: true,
};

interface QueryOptionsParams {
    isAuthenticated: boolean;
    userSub: string | undefined;
}

const transitionDialogDismissalQueryOptions = {
    queryKey: (userSub?: string) => [
        queryKeys.transitionDialogDismissal,
        userSub,
    ],
    queryFn: async ({
        isAuthenticated,
        userSub,
    }: QueryOptionsParams): Promise<TransitionDialogDismissalData> => {
        if (!isAuthenticated || !userSub) {
            return placeholderData;
        }

        const localStorageKey = await getLocalStorageUserSha(
            TRANSITION_DIALOG_DISMISSAL_KEY,
            userSub
        );

        // eslint-disable-next-line no-restricted-syntax
        const dismissedOn = localStorage.getItem(localStorageKey);

        if (!dismissedOn) {
            return { isDismissalDue: true };
        }

        const parsedDismissedOn = getUtcDateForMDY(dismissedOn);

        // A malformed stored value would otherwise produce an Invalid Date ->
        // NaN from daysAgo -> `NaN >= 7` is false, silently and permanently
        // suppressing the dialog. Treat that case the same as "never
        // dismissed".
        if (Number.isNaN(parsedDismissedOn.getTime())) {
            return { isDismissalDue: true };
        }

        return {
            isDismissalDue:
                daysAgo(parsedDismissedOn) >= DISMISSAL_INTERVAL_DAYS,
        };
    },
};

export function useTransitionDialogDismissal() {
    const { isAuthenticated, user } = useAuth();
    const { queryKey, queryFn } = transitionDialogDismissalQueryOptions;

    const { data, isFetched, ...otherProps } = useQuery({
        queryKey: queryKey(user?.sub),
        queryFn: () => queryFn({ isAuthenticated, userSub: user?.sub }),
        staleTime: Infinity,
    });

    const dismiss = useCallback(async () => {
        if (user?.sub) {
            const localStorageKey = await getLocalStorageUserSha(
                TRANSITION_DIALOG_DISMISSAL_KEY,
                user.sub
            );

            // eslint-disable-next-line no-restricted-syntax
            localStorage.setItem(localStorageKey, getYMD(new Date()));

            await invalidateQueryKey(queryKey(user.sub));
            return;
        }
        throw new Error(
            'useTransitionDialogDismissal.dismiss: called without an authenticated user'
        );
    }, [queryKey, user?.sub]);

    return {
        ...otherProps,
        ...(data ?? placeholderData),
        isFetched,
        dismiss,
    };
}
